import React, { useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { Drawer, message } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { FormattedMessage, useIntl } from "react-intl";
import { Context, Hooks, Util, isRtlLocale } from "@xt/xtbase";
import Telegram from "assets/images/home/Telegram.png";
import Facebook from "assets/images/home/Facebook.png";
import download from "assets/images/home/download.png";
import { captureSharePoster, preloadCanvasSafePosterImages } from "utils/captureSharePoster";
import { getReferralPosterDisplayUrl, preloadReferralPosterImages } from "utils/referralPosterUrl";
import { savePosterImage } from "utils/savePosterImage";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getOrigin } = Util;

const POSTER_SLIDES = [
  {
    key: "refer",
    image: getReferralPosterDisplayUrl("refer"),
    variant: "refer",
    subtitleKey: "home.sharePosterSubtitleRefer",
  },
  {
    key: "ai",
    image: getReferralPosterDisplayUrl("ai"),
    variant: "ai",
    subtitleKey: "home.sharePosterSubtitleAi",
  },
];

/** 底部 Actionsheet 内容区高度：海报底 → 分享按钮间距 38px */
const SHARE_SHEET_HEIGHT_SINGLE = 652;
const SHARE_SHEET_HEIGHT_SINGLE_NO_SUBTITLE = 596;
const SHARE_SHEET_HEIGHT_MULTI = 670;

/** Figma 海报宽度，固定 px，避免 slick 在 flex 下算出 0 宽 */
const POSTER_WIDTH = 310;

const SWIPE_THRESHOLD = 40;

/** 葡语 / 阿语海报主文案较长，略缩小字号避免压住插图 */
const COMPACT_POSTER_LANGS = ["pt", "ar"];

let scrollTop = 0;

function resolveInviteCode(qrUrl, inviteCodeProp) {
  if (inviteCodeProp != null && String(inviteCodeProp).trim() !== "") {
    return String(inviteCodeProp).trim();
  }
  if (!qrUrl || typeof qrUrl !== "string") return "";
  const str = qrUrl.trim();
  try {
    const url = new URL(str.startsWith("http") ? str : `https://${str}`);
    const ref = url.searchParams.get("ref");
    if (ref) return ref;
  } catch {
    /* ignore */
  }
  const m = str.match(/[?&]ref=([^&]+)/i);
  if (m) {
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }
  return "";
}

/** 渠道分享用纯文本：与海报主标题一致（去掉 highlight 标签） */
function getPosterShareText(slide, formatMessage) {
  if (!slide || typeof formatMessage !== "function") return "";
  const plainHighlight = (chunks) => (Array.isArray(chunks) ? chunks.join("") : String(chunks ?? ""));

  if (slide.variant === "aicopy" && slide.posterData) {
    const { strategyName, returnRate } = slide.posterData;
    const following = formatMessage({ id: "aicopy.sharePosterFollowing" }, { strategyName: strategyName || "—" });
    const rateLabel = formatMessage({ id: "aicopy.returnRatePast90Days" });
    return [following, `${rateLabel} ${returnRate || "0%"}`].filter(Boolean).join(" ");
  }

  if (slide.variant === "position" && slide.posterData) {
    const { pair, pnlValue, pnlUnit } = slide.posterData;
    const base = formatMessage({ id: "cfd.sharePosterText" });
    return [base, pair, pnlValue ? `${pnlValue} ${pnlUnit || ""}`.trim() : ""].filter(Boolean).join(" ");
  }

  if (slide.variant === "pair" && slide.posterData) {
    const { pair, latestPrice } = slide.posterData;
    const base = formatMessage({ id: "cfd.sharePairPosterText" });
    return [base, pair, latestPrice && latestPrice !== "—" ? latestPrice : ""].filter(Boolean).join(" ");
  }

  if (slide.variant === "aicopyOrders") {
    return formatMessage({ id: "aicopy.shareModalTitle" });
  }

  if (slide.variant === "ai") {
    const line1 = formatMessage({ id: "home.sharePosterAiHeadline1" }, { highlight: plainHighlight });
    const line2 = formatMessage({ id: "home.sharePosterAiHeadline2" });
    const sub = formatMessage({ id: "home.sharePosterAiHeadlineSub" }, { highlight: plainHighlight });
    return [line1, line2, sub].filter(Boolean).join(" ");
  }

  return formatMessage({ id: "home.sharePosterReferHeadline" }, { highlight: plainHighlight });
}

/** Figma 15225:51754 持仓/交易对海报左上角头像 */
function PosterUserAvatar({ clipId }) {
  const clipPathId = clipId || "share_poster_avatar_clip";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <g clipPath={`url(#${clipPathId})`}>
        <path
          d="M12.0002 0.382141C5.58376 0.382141 0.382324 5.58358 0.382324 12C0.382324 18.4165 5.58376 23.6179 12.0002 23.6179C18.4166 23.6179 23.6181 18.4165 23.6181 12C23.6181 5.58358 18.4166 0.382141 12.0002 0.382141ZM12.0004 20.0576C7.51182 20.0576 5.62914 17.4341 5.62914 17.4341L10.8759 14.8108C10.8759 14.8108 7.87782 12.6 7.87782 9.1892C7.87782 4.42145 10.7321 3.94239 12.0002 3.94239C13.2683 3.94239 16.1228 4.29358 16.1228 9.1892C16.1228 12.8878 13.1246 14.8108 13.1246 14.8108L18.3714 17.809C18.3714 17.809 16.4889 20.0576 12.0004 20.0576Z"
          fill="white"
          fillOpacity="0.32"
        />
      </g>
      <defs>
        <clipPath id={clipPathId}>
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function PosterUserRow({ userName, shareTime, avatarClipId }) {
  return (
    <div className={styles.posterPositionUser}>
      <div className={styles.positionAvatar}>
        <PosterUserAvatar clipId={avatarClipId} />
      </div>
      <div className={styles.positionUserMeta}>
        <p className={styles.positionUserName}>{userName}</p>
        <p className={styles.positionShareTime}>{shareTime}</p>
      </div>
    </div>
  );
}

function PosterHeadlineAicopy({ posterData, t, formatMessage }) {
  const { strategyName = "", returnRate = "0%", aum = "0" } = posterData || {};
  const followingText = formatMessage({ id: "aicopy.sharePosterFollowing" }, { strategyName: strategyName || "—" });

  return (
    <>
      <div className={styles.posterHeadlineAicopy}>
        <p className={styles.aicopyFollowing}>{followingText}</p>
        <div className={styles.aicopyRateBlock}>
          <p className={styles.aicopyRateLabel}>{t("aicopy.returnRatePast90Days")}</p>
          <p className={styles.aicopyRateValue}>{returnRate}</p>
        </div>
      </div>
      <div className={styles.posterAumBlock}>
        <p className={styles.aicopyAumLabel}>{t("aicopy.assetsUnderManagement")}</p>
        <p className={styles.aicopyAumValue}>{aum}</p>
      </div>
    </>
  );
}

function PosterHeadlinePosition({ posterData, t }) {
  const {
    userName = "",
    shareTime = "",
    pair = "",
    productName = "",
    sideText = "",
    leverage = "",
    pnlValue = "0",
    pnlUnit = "USDT",
    isNegative = false,
    entryPrice = "—",
    latestPrice = "—",
  } = posterData || {};

  return (
    <div className={styles.posterPositionContent}>
      <PosterUserRow userName={userName} shareTime={shareTime} avatarClipId="share_position_avatar_clip" />
      <div className={styles.posterPositionBody}>
        <div className={styles.positionPairRow}>
          <span className={styles.positionPair}>{pair}</span>
          {productName ? <span className={styles.positionProduct}>{productName}</span> : null}
        </div>
        <div className={styles.positionSideRow}>
          <span>{sideText}</span>
          <span className={styles.positionDivider}>|</span>
          <span>{leverage}</span>
        </div>
        <div className={styles.positionPnlRow}>
          <span className={cx(styles.positionPnlValue, isNegative && styles.positionPnlNegative)}>{pnlValue}</span>
          <span className={styles.positionPnlUnit}>{pnlUnit}</span>
        </div>
        <div className={styles.positionPriceRow}>
          <div className={styles.positionPriceItem}>
            <p className={styles.positionPriceLabel}>{t("cfd.entryPrice")}</p>
            <p className={styles.positionPriceVal}>{entryPrice}</p>
          </div>
          <div className={styles.positionPriceItem}>
            <p className={styles.positionPriceLabel}>{t("cfd.latestPrice")}</p>
            <p className={styles.positionPriceVal}>{latestPrice}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 交易对分享：同 poster-position 底图，无方向/杠杆/盈亏/开仓价等持仓信息 */
function PosterHeadlinePair({ posterData, t }) {
  const {
    userName = "",
    shareTime = "",
    pair = "",
    productName = "",
    latestPrice = "—",
  } = posterData || {};

  return (
    <div className={styles.posterPairContent}>
      <PosterUserRow userName={userName} shareTime={shareTime} avatarClipId="share_pair_avatar_clip" />
      <div className={styles.posterPairBody}>
        <div className={styles.pairSymbolRow}>
          <span className={styles.pairSymbol}>{pair}</span>
          {productName ? <span className={styles.pairProductTag}>{productName}</span> : null}
        </div>
        <div className={styles.pairLatestBlock}>
          <p className={styles.pairLatestLabel}>{t("cfd.latestPrice")}</p>
          <p className={styles.pairLatestValue}>{latestPrice}</p>
        </div>
      </div>
    </div>
  );
}

function PosterHeadlineAicopyOrders({ posterData, t }) {
  const orders = Array.isArray(posterData?.orders) ? posterData.orders : [];
  return (
    <div className={styles.posterAicopyOrders}>
      <p className={styles.aicopyOrdersTitle}>{t("aicopy.currentOrders")}</p>
      {orders.map((order) => (
        <div key={order.id || order.name} className={styles.aicopyOrderRow}>
          <span className={styles.aicopyOrderName}>{order.name}</span>
          <span className={styles.aicopyOrderReturn}>{order.returnRate}</span>
        </div>
      ))}
    </div>
  );
}

function PosterHeadline({ variant, isCompact }) {
  const highlight = (chunks) => <span className={styles.highlight}>{chunks}</span>;

  if (variant === "ai") {
    return (
      <div className={cx(styles.posterHeadline, styles.posterHeadlineAi, isCompact && styles.posterHeadlineCompact)}>
        <div className={styles.headlineMain}>
          <p className={styles.headlineLine}>
            <FormattedMessage id="home.sharePosterAiHeadline1" values={{ highlight }} />
          </p>
          <p className={styles.headlineLine}>
            <FormattedMessage id="home.sharePosterAiHeadline2" />
          </p>
        </div>
        <p className={styles.headlineSub}>
          <FormattedMessage id="home.sharePosterAiHeadlineSub" values={{ highlight }} />
        </p>
      </div>
    );
  }

  return (
    <div className={cx(styles.posterHeadline, styles.posterHeadlineRefer, isCompact && styles.posterHeadlineCompact)}>
      <p className={styles.headlineLine}>
        <FormattedMessage id="home.sharePosterReferHeadline" values={{ highlight }} />
      </p>
    </div>
  );
}

function ShareModal(props) {
  const t = useTranslation();
  const { formatMessage } = useIntl();
  const [appState] = React.useContext(XtContext);
  const lang = appState?.lang;
  const isRtl = isRtlLocale(lang);
  const isCompactPoster = COMPACT_POSTER_LANGS.includes(lang);

  const { isShowModal, onClose, qrUrl, inviteCode: inviteCodeProp, slides, renderPoster, hideSubtitle } = props;
  const posterSlides = slides || POSTER_SLIDES;
  const sheetHeight =
    posterSlides.length > 1
      ? SHARE_SHEET_HEIGHT_MULTI
      : hideSubtitle
        ? SHARE_SHEET_HEIGHT_SINGLE_NO_SUBTITLE
        : SHARE_SHEET_HEIGHT_SINGLE;
  const [activeIndex, setActiveIndex] = useState(0);
  const [savePreviewUrl, setSavePreviewUrl] = useState("");
  const [downloading, setDownloading] = useState(false);
  const touchStartXRef = useRef(null);

  const inviteCode = useMemo(() => resolveInviteCode(qrUrl, inviteCodeProp), [qrUrl, inviteCodeProp]);
  const shareLink = useMemo(() => {
    if (qrUrl && typeof qrUrl === "string" && qrUrl.startsWith("http")) return qrUrl;
    if (qrUrl && typeof qrUrl === "string" && qrUrl.includes("?")) return qrUrl;
    const origin = getOrigin("www") || "";
    if (!inviteCode) return origin;
    const sep = origin.endsWith("/") ? "" : "/";
    return `${origin}${sep}accounts/register?ref=${encodeURIComponent(inviteCode)}`;
  }, [qrUrl, inviteCode]);

  useEffect(() => {
    if (isShowModal) {
      setActiveIndex(0);
      setSavePreviewUrl("");
      preloadReferralPosterImages();
      preloadCanvasSafePosterImages();
    }
  }, [isShowModal]);

  useEffect(() => {
    if (!isShowModal) return undefined;
    scrollTop = document.documentElement.scrollTop;
    document.body.classList.add("at-frozen");
    document.body.style.top = `${-scrollTop}px`;
    return () => {
      document.body.style.top = null;
      document.body.classList.remove("at-frozen");
      document.documentElement.scrollTop = scrollTop;
    };
  }, [isShowModal]);

  const activeSubtitle = t(posterSlides[activeIndex]?.subtitleKey) || "";
  const activeShareText = useMemo(() => {
    const slide = posterSlides[activeIndex];
    const text = getPosterShareText(slide, formatMessage).trim();
    return text || t("home.sharePosterSheetTitle") || "";
  }, [activeIndex, formatMessage, t, posterSlides]);

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null) return;
    const endX = e.changedTouches[0]?.clientX;
    if (endX == null) return;
    const delta = endX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0 && activeIndex < posterSlides.length - 1) {
      setActiveIndex((i) => i + 1);
    } else if (delta > 0 && activeIndex > 0) {
      setActiveIndex((i) => i - 1);
    }
  };

  const saveHint =
    lang === "cn"
      ? "长按图片保存到相册"
      : "Press and hold the image to save to your photo library";

  const capturePosterFromDom = async () => {
    const el = document.getElementById(`ShareModalImg-${activeIndex}`);
    if (!el || typeof window === "undefined") return null;
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(el, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#1c1d22",
      scale: 3,
      width: POSTER_WIDTH,
      height: 420,
    });
    return canvas.toDataURL("image/png");
  };

  const capturePosterForDownload = async (slide) => {
    const canvasParams = {
      variant: slide.variant,
      formatMessage,
      t,
      inviteCode,
      shareLink,
      isCompact: isCompactPoster,
      isRtl,
      bgImageUrl: slide.bgImageUrl,
      posterData: slide.posterData,
    };

    try {
      return await captureSharePoster(canvasParams);
    } catch (canvasErr) {
      console.warn("Canvas poster capture failed, fallback to DOM:", canvasErr);
      return capturePosterFromDom();
    }
  };

  const downloadImg = async () => {
    const slide = posterSlides[activeIndex];
    if (!slide || downloading) return;
    setDownloading(true);
    try {
      const imgUrl = await capturePosterForDownload(slide);
      if (!imgUrl) throw new Error("poster capture empty");
      const filename = `spikex_share_${slide.key || "poster"}_${Date.now()}.png`;
      await savePosterImage(imgUrl, filename, { onMobilePreview: setSavePreviewUrl });
    } catch (err) {
      console.error("下载图片失败，", err);
      message.error(t("home.sharePosterDownloadFailed") || "Failed to save poster. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const getShareLink = () => {
    if (shareLink && typeof shareLink === "string") return shareLink;
    return getOrigin("www");
  };

  const handleSocialShare = (platform) => {
    const websiteUrl = getShareLink();
    const shareText = activeShareText;
    let shareUrl = "";
    switch (platform) {
      case "twitter":
        shareUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(websiteUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(websiteUrl)}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(websiteUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(websiteUrl)}`;
        break;
      default:
        shareUrl = websiteUrl;
    }
    if (shareUrl) window.open(shareUrl, "_blank");
  };

  const renderPosterSlide = (slide, index) => {
    const useLightQr = ["aicopy", "position", "pair", "aicopyOrders"].includes(slide.variant);
    return (
    <div
      key={slide.key}
      id={`ShareModalImg-${index}`}
      className={cx(
        styles.posterCard,
        slide.variant === "aicopy" && styles.posterCardAicopy,
        slide.variant === "position" && styles.posterCardPosition,
        slide.variant === "pair" && styles.posterCardPair,
        slide.variant === "aicopyOrders" && styles.posterCardAicopy,
        isRtl && styles.posterCardRtl
      )}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <img src={slide.image} alt="" className={styles.posterBg} draggable={false} crossOrigin="anonymous" />
      {slide.variant === "aicopy" ? (
        <PosterHeadlineAicopy posterData={slide.posterData} t={t} formatMessage={formatMessage} />
      ) : slide.variant === "position" ? (
        <PosterHeadlinePosition posterData={slide.posterData} t={t} />
      ) : slide.variant === "pair" ? (
        <PosterHeadlinePair posterData={slide.posterData} t={t} />
      ) : slide.variant === "aicopyOrders" ? (
        <PosterHeadlineAicopyOrders posterData={slide.posterData} t={t} />
      ) : (
        <PosterHeadline variant={slide.variant} isCompact={isCompactPoster} />
      )}
      <div className={styles.posterFooter}>
        <div className={styles.posterFooterText}>
          <p className={styles.scanTip}>{t("home.sharePosterScanRegister")}</p>
          <p className={styles.codeRow}>
            <span>{t("home.sharePosterCodeLabel")}</span>
            <span className={styles.codeValue}>{inviteCode || "—"}</span>
          </p>
        </div>
      </div>
      <div className={styles.qrBox}>
        <QRCodeSVG
          value={shareLink || " "}
          size={50}
          fgColor="#000000"
          bgColor={useLightQr ? "#ffffff" : "transparent"}
          level="M"
          includeMargin={false}
        />
      </div>
    </div>
  );
  };

  return (
    <Drawer
      placement="bottom"
      open={isShowModal}
      onClose={onClose}
      closable={false}
      destroyOnClose
      title={null}
      height={sheetHeight}
      className={cx("referralShareDrawer", styles.drawer)}
      headerStyle={{ display: "none" }}
      bodyStyle={{ padding: 0, background: "#181a20", overflow: "hidden" }}
      maskStyle={{ background: "rgba(0, 0, 0, 0.4)" }}
    >
      <div className={cx("ShareModal", styles.sheet, isRtl && styles.sheetRtl)} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.handleBar}>
          <div className={styles.handle} aria-hidden />
        </div>
        <div className={styles.sheetTitle}>{props.title || t("home.sharePosterSheetTitle")}</div>
        {!hideSubtitle && <div className={styles.subtitle}>{activeSubtitle}</div>}

        <div className={styles.carouselWrap}>
          <div
            className={styles.carouselViewport}
            onTouchStart={posterSlides.length > 1 ? handleTouchStart : undefined}
            onTouchEnd={posterSlides.length > 1 ? handleTouchEnd : undefined}
          >
            <div
              className={styles.carouselTrack}
              style={{
                width: posterSlides.length * POSTER_WIDTH,
                transform: isRtl
                  ? `translateX(${activeIndex * POSTER_WIDTH}px)`
                  : `translateX(-${activeIndex * POSTER_WIDTH}px)`,
              }}
            >
              {posterSlides.map((slide, index) =>
                renderPoster
                  ? renderPoster(slide, index)
                  : renderPosterSlide(slide, index)
              )}
            </div>
          </div>
        </div>

        {posterSlides.length > 1 && (
          <div className={styles.dots} role="tablist" aria-label="poster slides">
            {posterSlides.map((slide, index) => (
              <button
                key={slide.key}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                className={cx(styles.dot, index === activeIndex && styles.dotActive)}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        )}

        <div className={styles.shareFoot}>
          <div className={styles.footItem} onClick={() => handleSocialShare("facebook")}>
            <img src={Facebook} alt="Facebook" />
            <span>Facebook</span>
          </div>
          <div className={styles.footItem} onClick={() => handleSocialShare("telegram")}>
            <img src={Telegram} alt="Telegram" />
            <span>Telegram</span>
          </div>
          <div className={styles.footItem} onClick={() => handleSocialShare("twitter")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <circle cx="16" cy="16" r="15.8" stroke="white" strokeOpacity="0.4" strokeWidth="0.4" />
              <path
                d="M20.1979 9H22.5729L17.3843 14.9303L23.4883 23H18.7089L14.9655 18.1057L10.6822 23H8.30585L13.8556 16.6569L8 9H12.9007L16.2844 13.4735L20.1979 9ZM19.3644 21.5785H20.6804L12.1856 10.3469H10.7734L19.3644 21.5785Z"
                fill="white"
                fillOpacity="0.4"
              />
            </svg>
            <span>X</span>
          </div>
          <div className={styles.footItem} onClick={() => handleSocialShare("email")}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <circle cx="16" cy="16" r="15.8" stroke="white" strokeOpacity="0.4" strokeWidth="0.4" />
              <path
                d="M16 16.8L8 11.808V11.2C8 10.312 8.712 9.59998 9.6 9.59998H22.4C22.8243 9.59998 23.2313 9.76855 23.5314 10.0686C23.8314 10.3687 24 10.7756 24 11.2V11.8L16 16.8ZM24 20.8C24 21.2243 23.8314 21.6313 23.5314 21.9313C23.2313 22.2314 22.8243 22.4 22.4 22.4H9.6C9.17565 22.4 8.76869 22.2314 8.46863 21.9313C8.16857 21.6313 8 21.2243 8 20.8V13.688L16 18.6496L24 13.688V20.8Z"
                fill="white"
                fillOpacity="0.4"
              />
            </svg>
            <span>Email</span>
          </div>
          <div
            className={cx(styles.footItem, downloading && styles.footItemDisabled)}
            onClick={downloading ? undefined : downloadImg}
          >
            <img src={download} alt="" />
            <span>{downloading ? t("activity.rewardClaimLoading") || "Loading..." : t("home.downLoad")}</span>
          </div>
        </div>

      </div>

      {savePreviewUrl ? (
        <div className={styles.savePreview} role="dialog" aria-modal="true">
          <button type="button" className={styles.savePreviewClose} onClick={() => setSavePreviewUrl("")} aria-label="close">
            ×
          </button>
          <p className={styles.savePreviewHint}>{saveHint}</p>
          <img src={savePreviewUrl} alt="" className={styles.savePreviewImg} />
        </div>
      ) : null}
    </Drawer>
  );
}

export default ShareModal;
